import { useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Lot } from '../../../domain/Lot';

// admin: lo abre el panel de admin (ve y edita como el dueño; sin borrar,
// sin compartir y sin aviso propio).
export function useLotDetail(id: string, admin = false) {
  const { lots: userRepo, admin: adminRepo } = useRepositories();
  const lotRepo = {
    get: (lotId: string) => (admin ? adminRepo.fetchLot(lotId) : userRepo.get(lotId)),
    update: (lotId: string, input: Parameters<typeof userRepo.update>[1]) =>
      admin ? adminRepo.updateLot(lotId, input) : userRepo.update(lotId, input),
    observeStage: (lotId: string, stageIndex: number) =>
      admin ? adminRepo.observeLotStage(lotId, stageIndex) : userRepo.observeStage(lotId, stageIndex),
    delete: (lotId: string) => userRepo.delete(lotId),
  };
  const [lot, setLot] = useState<Lot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = async () => {
    try {
      const { lot: found } = await lotRepo.get(id);
      setLot(found);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el lote.');
    } finally {
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
  });

  const run = async (action: () => Promise<{ lot: Lot }>) => {
    setBusy(true);
    setActionError(null);
    try {
      const { lot: updated } = await action();
      setLot(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  // Corrección con el campo: la etapa que el técnico vio hoy en el lote.
  const observeStage = (stageIndex: number) => run(() => lotRepo.observeStage(id, stageIndex));

  // Pasturas: el ciclo arranca de nuevo hoy (corte o pastoreo).
  const restartCycle = (today: string) => run(() => lotRepo.update(id, { cycleStart: today }));

  // Aviso (push) cuando el lote pasa a la etapa siguiente.
  const setNotifyStageChange = (notifyStageChange: boolean) => run(() => lotRepo.update(id, { notifyStageChange }));

  const remove = async (): Promise<boolean> => {
    setBusy(true);
    try {
      await lotRepo.delete(id);
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No se pudo eliminar el lote.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { lot, loading, error, busy, actionError, observeStage, restartCycle, setNotifyStageChange, remove };
}
